import { isGlobalFlow } from "./CommonUtils";
import {
  xdmPageTypeHelper,
} from "./Constants";

export const createEventDetail = (data = {}) => {
  const { tokenJson = {} } = data || {};
  const { tokensList = {} } = tokenJson || {};
  const { airline_code } = tokensList || {};
  
  const eventDetail = {
    event_type: "user_action",
    event_name: "page-rendered",
    event_value: "listing",
  };
  
  if (isValidToken(airline_code)) {
    eventDetail.components = [
      {
        id: airline_code,
      },
    ];
  }
  
  return {
    event_detail: eventDetail,
  };
};

export const createPageContext = (data = {}) => {
  const pageName = xdmPageTypeHelper[data?.pageType] || "funnel";
  let funnelStep = "listing";

  const templatePageNames = [
    "route",
    "airline",
    "city",
    "flight_schedule",
    "distance",
    "fare_calender",
    "airline_route",
    "airline_index",
    "airport",
    "city_country",
    "city_country_index",
    "city_airline",
  ];

  const landingPageNames = [
    "city_airport",
    "static",
    "offers",
    "index_landing",
    "index_city_airline",
    "index_city",
    "index_city_route",
    "index_city_name",
    "senior_citizens",
    "armed_forces",
  ];

  let pageType = "funnel";
  if (templatePageNames.includes(pageName)) {
    pageType = "template";
  } else if (landingPageNames.includes(pageName)) {
    pageType = "landing";
    funnelStep = "landing";
  }

  return {
    page_context: {
      lob: "flights",
      page_type: pageType,
      funnel_step: funnelStep,
      page_name: pageName,
      lob_category: data.isIntl ? "if" : "df",
    },
  };
};

export const createBaseExtraOptions = (data = {}) => {
  return {
    lob: data.lob || "flights",
    destinationUrl: data.destinationUrl || "",
  };
};

export const createDestinationUrlOptions = (data = {}) => {
  return {
    destinationUrl: data.destinationUrl || "",
  };
};

export const createLobOptions = (data = {}) => {
  return {
    lob: data.lob || "flights",
  };
};

export const mergeObjects = (...objects) => {
  return objects.reduce((acc, obj) => ({ ...acc, ...obj }), {});
};

export const createEventData = (data) => {
  const eventDetailObj = createEventDetail(data);
  const pageContextObj = createPageContext(data);

  const combinedEventData = mergeObjects(eventDetailObj, pageContextObj);

  const baseExtraOptions = createBaseExtraOptions(data);
  const destinationUrlOptions = createDestinationUrlOptions(data);
  const lobOptions = createLobOptions(data);

  const combinedExtraOptions = mergeObjects(
    baseExtraOptions,
    destinationUrlOptions,
    lobOptions,
  );

  return {
    eventData: combinedEventData,
    extraOptions: combinedExtraOptions,
  };
};

const isValidToken = (token) => token && token.trim() !== "";

const convertMMDDYYYYToEpoch = (dateNumber) => {
  if (!dateNumber) return null;

  const dateString = dateNumber.toString();

  if (dateString.length !== 8) return null;

  const month = parseInt(dateString.substring(0, 2), 10);
  const day = parseInt(dateString.substring(2, 4), 10);
  const year = parseInt(dateString.substring(4, 8), 10);

  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  return Math.floor(date.getTime() / 1000);
};

const createLocationObject = (locusId, locationCode, cityData) => {

  return {
    locus: {
      locus_id: locusId,
      locus_type: "city",
    },
    location: {
      code: locationCode,
      name: cityData?.name,
      country_code: cityData?.country_id,
    },
  };
};

const handleSingleCityContext = (tokensList, pageType) => {
  const { locus_city_code, city_code, city_name, country_code } = tokensList || {};
  const cityData = {
    name: city_name,
    country_id: country_code,
  }

  const locationObject = createLocationObject(
    locus_city_code,
    city_code,
    cityData,
  );

  return pageType === "fromPage"
    ? { from: locationObject }
    : pageType === "toPage"
    ? { to: locationObject }
    : {};
};

const handleRouteContext = (tokensList, isIntl) => {
  const { src_locus_code, dest_locus_code, src_city_code, dest_city_code, src_city_name, src_country_code, dest_city_name, dest_country_code } = tokensList || {};
  
  // For DF routes (isIntl === false), default to "IN" if country codes are not available
  // For IF routes, use null if country codes are missing
  const defaultCountryCode = isIntl === false ? "IN" : null;
  
  const fromCityData = {
    name: src_city_name,
    country_id: src_country_code || defaultCountryCode,
  }
  const toCityData = {
    name: dest_city_name,
    country_id: dest_country_code || defaultCountryCode,
  }
  return {
    from: createLocationObject(src_locus_code, src_city_code, fromCityData),
    to: createLocationObject(dest_locus_code, dest_city_code, toCityData),
  };
};

const handleNonGobiboSingleCityContext = (tokensList) => {
  const { locus_city_code, city_code, city_name, country_code } = tokensList || {};
  const toCityData = {
    name: city_name,
    country_id: country_code,
  }
  const locationObject = createLocationObject(
    locus_city_code,
    city_code,
    toCityData,
  );

  return { to: locationObject };
};

const getFutureDateEpoch = (isIntl) => {
  const futureDate = new Date();
  const daysToAdd = isIntl ? 10 : 1;
  futureDate.setDate(futureDate.getDate() + daysToAdd);
  return Math.floor(futureDate.getTime() / 1000);
};

const createBaseSearchContextProperties = (data) => {
  const { tokenJson = {}, isIntl } = data || {};
  const { tokensList = {} } = tokenJson;
  const { cheap_MMddyyyy } = tokensList;

  let fromDateTime;
  if (isValidToken(cheap_MMddyyyy)) {
    fromDateTime = convertMMDDYYYYToEpoch(cheap_MMddyyyy);
    if (fromDateTime === null) {
      fromDateTime = getFutureDateEpoch(isIntl);
    }
  } else {
    fromDateTime = getFutureDateEpoch(isIntl);
  }

  const isGlobal = isGlobalFlow(data);

  return {
    advance_purchase: isGlobal ? "2" : 2,
    from_date_time: fromDateTime,
    pax: {
      count: 1,
      details: {
        adult: {
          count: 1,
        },
        child: {
          count: 0,
        },
      },
    },
  };
};

const hasValidSearchContextData = (searchContextContent) => {
  const hasFrom = searchContextContent.from && Object.keys(searchContextContent.from).length > 0;
  const hasTo = searchContextContent.to && Object.keys(searchContextContent.to).length > 0;
  return { hasFrom, hasTo };
};

const buildSearchContextStructure = (searchContextContent, hasFrom, hasTo, data) => {
  const baseProperties = createBaseSearchContextProperties(data);

  if (!hasFrom && !hasTo) {
    return {
      search_context: baseProperties,
    };
  }

  const searchContext = { ...baseProperties };

  if (hasFrom) {
    searchContext.from = searchContextContent.from;
  }

  if (hasTo) {
    searchContext.to = searchContextContent.to;
  }

  return {
    search_context: searchContext,
  };
};

const determineSearchContextContent = (
  tokensList,
  data,
  isGoibibo,
  pageType,
  hasLocusCityCode,
  hasSrcCode,
  hasDesCode,
) => {
  // isIntl is false for DF routes, true for IF routes
  const isIntl = data?.isIntl !== false; // Default to true (IF) if not explicitly set to false
  
  if (isGoibibo) {
    if (hasLocusCityCode && !hasSrcCode && !hasDesCode) {
      return handleSingleCityContext(tokensList, pageType);
    } else if (hasSrcCode && hasDesCode) {
      return handleRouteContext(tokensList, isIntl);
    }
  } else {
    if (hasLocusCityCode && !hasSrcCode && !hasDesCode) {
      return handleNonGobiboSingleCityContext(tokensList);
    } else if (hasSrcCode && hasDesCode) {
      return handleRouteContext(tokensList, isIntl);
    }
  }
  return {};
};

export const createSearchContext = (data = {}) => {
  const { tokenJson = {}, isGoibibo, pageType } = data;
  const { tokensList = {} } = tokenJson || {};
  const { locus_city_code, src_locus_code, dest_locus_code } = tokensList || {};
  const hasLocusCityCode = isValidToken(locus_city_code);
  const hasSrcCode = isValidToken(src_locus_code);
  const hasDesCode = isValidToken(dest_locus_code);

  const searchContextContent = determineSearchContextContent(
    tokensList,
    data,
    isGoibibo,
    pageType,
    hasLocusCityCode,
    hasSrcCode,
    hasDesCode,
  );
  
  const { hasFrom, hasTo } = hasValidSearchContextData(searchContextContent) || {};

  return buildSearchContextStructure(searchContextContent, hasFrom, hasTo, data);
};

export const createDefaultEventData = (data = {}) => {

  const eventDetailObj = createEventDetail(data);
  const pageContextObj = createPageContext(data);
  const searchContextObj = createSearchContext(data);


  const combinedEventData = mergeObjects(
    eventDetailObj, 
    pageContextObj, 
    searchContextObj, 
  );


  const baseExtraOptions = createBaseExtraOptions(data);
  const destinationUrlOptions = createDestinationUrlOptions(data);
  const lobOptions = createLobOptions(data);


  const combinedExtraOptions = mergeObjects(
    baseExtraOptions,
    destinationUrlOptions,
    lobOptions,
  );

  return {
    eventData: combinedEventData,
    extraOptions: combinedExtraOptions,
  };
};

export const createContentDetail = (id, type = "", position = { v: 0, h: 0 }) => {
  return {
    id,
    type,
    position,
  };
};
